import * as React from 'react';
import moment from 'moment';
import styled, { css } from 'styled-components';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';

import InputWithRifm from '../inputForm/InputForm';

import dateRecipe, { getFormatWithSeparator } from '../rifmRecipes/date';
import { CalendarProps } from './models';

const defaultErrorMessage = 'Please enter a valid date';

const defaultFormat = 'MM/DD/YYYY';
const defaultSeparator = '/';
const datePickerDefaultFormat = 'm/d/Y';

// tslint:disable-next-line:variable-name
const SDatePickerWrap = styled.div<{ isBlockView: boolean }>(
  ({ theme, isBlockView }) => css`
    display: flex;
    position: relative;
    flex-grow: 1;
    flex-shrink: 1;
    flex-direction: column;
    @media only screen and (min-width: 800px) {
      flex-basis: 0;
    }

    .flatpickr-input[readonly] {
      display: none;
    }

    .inputLabel {
      color: #000000;
    }

    ${!isBlockView &&
    css`
      .flatpickr-calendar.inline {
        z-index: 1;
        margin-top: 75px;
        position: absolute;
      }
    `}
  `
);

type CalendarState = {
  open: boolean;
  date: string;
  isDateValid: boolean;
  dateValid: string;
  dateForPicker: string;
};

// tslint:disable-next-line:variable-name
export const StyledFlatpickr = styled(
  ({ date, handleDateChosen, maxDate, minDate, calenderArrows, firstDayOfWeek = 0 }) => (
    <Flatpickr
      options={{
        locale: {
          firstDayOfWeek: firstDayOfWeek,
        },
        dateFormat: datePickerDefaultFormat,
        inline: true,
        maxDate: maxDate,
        minDate: minDate,
        ...(calenderArrows && { nextArrow: calenderArrows.nextArrow, prevArrow: calenderArrows.prevArrow }),
      }}
      value={date}
      onChange={handleDateChosen}
    />
  )
)`
  &.flatpickr-calendar.inline {
    border: none;
  }
`;
const defaultDate = '01/01/1990';
export default class DatePicker extends React.Component<CalendarProps, CalendarState> {
  public node = React.createRef<HTMLDivElement>();

  constructor(props: CalendarProps) {
    super(props);

    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.state = {
      date: props.value || (props.noDefaultValue ? '' : defaultDate),
      isDateValid: props.isDateValid,
      open: props.isBlockView || false,
      dateValid: '',
      dateForPicker: props.value || (props.noDefaultValue ? '' : defaultDate),
    };
  }

  get format() {
    const { formatProps } = this.props;

    return formatProps?.format || defaultFormat;
  }

  get separator() {
    const { formatProps } = this.props;

    return formatProps?.separator || defaultSeparator;
  }

  public componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  public componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  private handleClickOutside(event: MouseEvent) {
    if (this.state.open && this.node.current) {
      if (!this.node.current.contains(event.target as Node)) {
        this.handleDatePickerOpenState(false);
      }
    }
  }

  private handleDatePickerOpenState = (newState: boolean) => {
    this.setState(() => ({ open: this.props.isBlockView || newState }));
  };

  public toggleCalendar = () => {
    this.handleDatePickerOpenState(!this.state.open);
  };

  public handleOnKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // added option for accessibillity
    if (event.key === 'Escape') {
      this.toggleCalendar();
    }
  };

  private checkMinMaxDate = (dateChosen: string) => {
    const currentDate = new Date(dateChosen);
    const today = new Date();
    const minDate = this.props.minDate || new Date('01/01/1900');
    today.setHours(0, 0, 0, 0);
    if (currentDate >= today || currentDate <= minDate) {
      return true;
    }
    return false;
  };

  private validateDate = (date: string) => {
    // If a user type invalid date / future date
    if (
      !/^(0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])[\/\-]\d{4}$/i.test(date) ||
      date.length !== 10 ||
      this.checkMinMaxDate(date)
    ) {
      if (!/^(0[1-9]|1[012])(0[1-9]|[12][0-9]|3[01])\d{4}$/i.test(date)) {
        return false;
      }
    }
    return true;
  };

  private onValueChange = (value: string) => {
    // Reformat value from scratch (it's broken in case char is added in the middle)
    const formattedValue = getFormatWithSeparator(this.separator)((value || '').replaceAll(this.separator, ''));

    // If a user type more than allowed chars don't do anything
    if (formattedValue?.length <= 10) {
      const dateIsValid = this.validateDate(formattedValue);
      if (dateIsValid) {
        const isDefaultFormat = this.format === defaultFormat;

        const formattedDate = isDefaultFormat
          ? formattedValue
          : moment(formattedValue, defaultFormat).format(this.format);

        this.props.onChange(formattedDate, this.props.name);
        this.setState(() => ({
          date: formattedValue,
          dateValid: '',
          isDateValid: true,
          dateForPicker: formattedValue,
        }));
      } else {
        this.props.onChange('', this.props.name);
        this.setState(() => ({
          date: formattedValue,
          dateValid: defaultErrorMessage,
          dateForPicker: this.props.noDefaultValue ? '' : defaultDate,
          isDateValid: false,
        }));
        this.handleDatePickerOpenState(false);
      }
    }
  };

  private handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.onValueChange(event.target?.value);
  };

  public handleDateChosen = ([date]: Date[]) => {
    const dateString = moment(date).format(this.format);

    this.props.onChange(dateString, this.props.name);
    this.setState(() => ({
      date: dateString,
      dateForPicker: dateString,
      dateValid: '',
      isDateValid: true,
    }));
    this.handleDatePickerOpenState(false);
  };

  public handleOnClick = () => {
    if (!this.state.open) {
      this.toggleCalendar();
    }

    // Populate the data on a first user click with the date already set from a previous application
    if (this.props.value !== '' && this.state.date === '') {
      this.setState(() => ({ date: this.props.value, dateForPicker: this.props.value }));
    }
  };

  public render() {
    const {
      label,
      value,
      placeholder,
      animatedLabel,
      onBlur,
      initErrorSpacing,
      className,
      inputDataTestId,
      maxDate = new Date(),
      minDate = new Date('01/01/1900'),
      error,
      calenderArrows,
      firstDayOfWeek,
      pattern = '.*',
      isBlockView,
      disabled,
    } = this.props;
    const { date, open, dateValid, dateForPicker } = this.state;

    return (
      <SDatePickerWrap
        data-at="dateDiv"
        className={className}
        ref={this.node}
        onClick={this.handleOnClick}
        onKeyUp={this.handleOnKeyUp}
        isBlockView={isBlockView}
      >
        <InputWithRifm
          {...{
            dataTestId: inputDataTestId,
            initErrorSpacing,
            onBlur,
            onChange: this.handleInputChange,
            value: date,
            dataAt: 'dateInput',
            disabled,
            name: this.props.name || 'datePicker',
            label: animatedLabel ? '' : label || '',
            placeholder: value || placeholder || 'Date of Birth',
            maxLength: this.format.length,
            rifm: {
              accept: dateRecipe.accept,
              format: getFormatWithSeparator(this.separator),
              onChange: this.onValueChange,
            },
            error: dateValid || error,
            pattern,
          }}
        />

        {open && (
          <StyledFlatpickr
            date={dateForPicker}
            data-at="calendarInput"
            className="{calendarClassNames}"
            maxDate={maxDate}
            minDate={minDate}
            handleDateChosen={this.handleDateChosen}
            calenderArrows={calenderArrows}
            firstDayOfWeek={firstDayOfWeek}
          />
        )}
      </SDatePickerWrap>
    );
  }
}
